<template>
  <div class="min-h-full p-4">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="flex items-center justify-between mb-8">
        <button
          type="button"
          @click="goBack"
          class="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-3 py-1.5 rounded-xl border border-indigo-400 border-opacity-20 font-medium transition-all duration-300 flex items-center gap-2 hover:from-indigo-600 hover:to-indigo-700"
        >
          <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          返回
        </button>
        <h1 class="text-2xl font-bold text-gray-900">订单列表</h1>
        <div class="w-16"></div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="glass-effect rounded-3xl p-12 shadow-xl border border-white border-opacity-20 text-center">
        <div class="flex items-center justify-center gap-2 text-blue-600">
          <svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
          <span class="font-medium">加载中...</span>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="orders.length === 0" class="glass-effect rounded-3xl p-12 shadow-xl border border-white border-opacity-20 text-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-gray-300 mx-auto mb-4"><path d="M16 16h6"/><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"/><path d="m7.5 4.27 9 5.15"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/></svg>
        <p class="text-gray-500 text-lg">暂无订单记录</p>
        <div class="mt-4 bg-amber-50 rounded-xl p-4 text-left">
          <p class="text-amber-700 text-sm font-medium mb-1">没有找到您的订单？</p>
          <p class="text-amber-600 text-sm">订单与当前登录账号关联。请使用下单时同一账号登录后查看；若曾以匿名方式产生的历史订单，需联系管理员核对。</p>
        </div>
      </div>

      <!-- Order List -->
      <div v-else>
        <div class="space-y-4">
        <div
          v-for="order in orders"
          :key="order.order_no"
          class="glass-effect rounded-2xl p-6 shadow-lg border border-white border-opacity-20 hover:shadow-xl transition-shadow overflow-visible"
        >
          <div class="flex items-start justify-between mb-3">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-sm font-mono text-gray-500 truncate">{{ order.order_no }}</span>
              </div>
              <div v-if="order.original_name" class="text-base font-medium text-gray-900 truncate">
                {{ order.original_name }}
              </div>
            </div>
            <div class="flex items-center gap-2 ml-4 shrink-0 overflow-visible flex-wrap justify-end">
              <span :class="statusClass(order.status)" class="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                {{ statusText(order.status) }}
              </span>
              <template v-if="hasRefundRequestBadge(order)">
                <div
                  v-if="order.refund_request_status === 'rejected'"
                  class="group relative inline-block max-w-full"
                >
                  <span
                    :class="refundRequestStatusClass(order.refund_request_status)"
                    class="inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                  >
                    {{ refundRequestStatusText(order.refund_request_status) }}
                  </span>
                  <div
                    class="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1.5 w-max max-w-[min(280px,calc(100vw-2rem))] -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-left text-xs leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
                    role="tooltip"
                  >
                    <span class="text-gray-400">拒绝原因：</span>{{ rejectReasonDisplay(order) }}
                  </div>
                </div>
                <span
                  v-else
                  :class="refundRequestStatusClass(order.refund_request_status)"
                  class="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                >
                  {{ refundRequestStatusText(order.refund_request_status) }}
                </span>
              </template>
              <button
                v-if="order.status === 'paid' && order.price > 0 && !order.refund_request_status"
                @click="handleRefund(order)"
                :disabled="refundSubmitting === order.order_no"
                class="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {{ refundSubmitting === order.order_no ? '提交中...' : '申请退款' }}
              </button>
              <button
                v-if="canDownloadResult(order)"
                type="button"
                @click="downloadResult(order)"
                :disabled="downloading === order.order_no"
                class="px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap inline-flex items-center gap-1"
              >
                <IconDownload :size="14" />
                {{ downloading === order.order_no ? '下载中...' : '下载结果' }}
              </button>
              <span
                v-else-if="order.task_status === 'running'"
                class="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-blue-100 text-blue-700"
              >处理中</span>
              <span
                v-else-if="order.task_status === 'failed'"
                class="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-orange-100 text-orange-700"
              >处理失败</span>
            </div>
          </div>
          <div class="flex items-center gap-6 text-sm text-gray-500 flex-wrap">
            <span>{{ order.quantity }} 条</span>
            <span v-if="canDownloadResult(order)">查得 {{ order.found_count ?? 0 }} 条</span>
            <span v-if="order.price > 0" class="font-semibold text-gray-700">¥{{ order.price.toFixed(2) }}</span>
            <span v-else class="text-green-600 font-semibold">免费</span>
            <span class="ml-auto">{{ order.created_at }}</span>
          </div>
        </div>
        </div>

        <div v-if="orders.length > 0" class="flex flex-col items-center pt-4 pb-2">
          <button
            v-if="hasMoreOrders"
            type="button"
            :disabled="loadingMore"
            @click="loadMoreOrders"
            class="px-6 py-2.5 rounded-xl text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loadingMore ? '加载中...' : '查看更多' }}
          </button>
          <p v-else class="text-sm text-gray-500">没有更多订单了</p>
        </div>
      </div>

      <!-- Error -->
      <div v-if="error" class="mt-4 bg-red-50 text-red-600 rounded-xl p-4 text-center text-sm">
        {{ error }}
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="toast"
        class="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg text-white font-semibold max-w-[min(90vw,28rem)] text-center"
        :class="toastError ? 'bg-red-500' : 'bg-blue-600'"
        role="alert"
      >
        {{ toast }}
      </div>
    </transition>

    <!-- Refund Confirm Dialog -->
    <transition name="fade">
      <div v-if="showRefundDialog" class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" @click.self="cancelRefund">
        <div class="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
          <div class="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-red-500"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 text-center mb-2">申请退款</h3>
          <p class="text-sm text-gray-600 text-center mb-1">提交后由客服审核处理；可填写退款原因（选填）。</p>
          <p class="text-center text-2xl font-bold text-red-600 mb-4">¥{{ refundTarget?.price?.toFixed(2) }}</p>
          <label class="block text-sm font-medium text-gray-700 mb-1.5">退款原因 <span class="text-gray-400 font-normal">（选填）</span></label>
          <textarea
            v-model="refundReason"
            rows="4"
            maxlength="512"
            placeholder="选填，填写后便于尽快处理"
            class="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 resize-y min-h-[96px]"
          />
          <p class="mt-1 text-xs text-gray-400 text-right">{{ refundReason.length }}/512</p>
          <div class="flex gap-3 mt-4">
            <button
              type="button"
              @click="cancelRefund"
              :disabled="refundSubmitting !== ''"
              class="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
            >取消</button>
            <button
              type="button"
              @click="confirmRefund"
              :disabled="refundSubmitting !== ''"
              class="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {{ refundSubmitting !== '' ? '提交中...' : '提交申请' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { isSuccessResponse, extractData, handleApiError, BusinessCode } from '../utils/api.js';
import { isLoggedIn } from '../utils/auth.js';
import IconDownload from '../components/icons/IconDownload.vue';

const router = useRouter();
const orders = ref([]);
const loading = ref(true);
const loadingMore = ref(false);
const page = ref(1);
const pageSize = 5;
const totalCount = ref(0);
const error = ref('');

const hasMoreOrders = computed(() => totalCount.value > orders.value.length);
const refundSubmitting = ref('');
const showRefundDialog = ref(false);
const refundTarget = ref(null);
const refundReason = ref('');
const downloading = ref('');
const toast = ref('');
const toastError = ref(true);
let toastTimer = null;

const FILE_MISSING_HINT = '处理结果文件不存在或已过期，无法下载';

const goBack = () => {
  router.push('/');
};

const statusText = (status) => {
  if (status === 'refunding') return '已支付';
  const map = {
    pending: '待支付',
    paid: '已支付',
    cancelled: '已取消',
    refunded: '已退款',
  };
  return map[status] || status;
};

const statusClass = (status) => {
  if (status === 'refunding') return 'bg-green-100 text-green-700';
  const map = {
    pending: 'bg-yellow-100 text-yellow-700',
    paid: 'bg-green-100 text-green-700',
    cancelled: 'bg-gray-100 text-gray-500',
    refunded: 'bg-red-100 text-red-600',
  };
  return map[status] || 'bg-gray-100 text-gray-500';
};

/** 退款申请状态（与后端 refund_requests.status 一致），无申请为空字符串 */
const refundRequestStatusText = (s) => {
  const v = (s || '').trim();
  if (!v) return '—';
  const map = {
    pending: '待处理',
    approved: '已批准',
    rejected: '拒绝退款',
    done: '已完成',
  };
  return map[v] || v;
};

const refundRequestStatusClass = (s) => {
  const v = (s || '').trim();
  if (!v) return 'bg-gray-50 text-gray-400 border border-gray-100';
  const map = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-slate-100 text-slate-600',
    done: 'bg-blue-100 text-blue-800',
  };
  return map[v] || 'bg-gray-100 text-gray-600';
};

/** 后端字段 refund_reject_reason；兼容驼峰 */
const rawRefundRejectReason = (order) => {
  if (!order) return '';
  const v = order.refund_reject_reason ?? order.refundRejectReason;
  return typeof v === 'string' ? v : '';
};

const rejectReasonDisplay = (order) => {
  const t = rawRefundRejectReason(order).trim();
  return t || '未填写';
};

/** 有退款申请记录时才显示退款申请徽章；已退款订单不显示 */
const hasRefundRequestBadge = (order) => {
  if (!order || order.status === 'refunded') return false;
  const v = (order.refund_request_status ?? '').toString().trim();
  return v.length > 0;
};

const fetchOrders = async (isInitial) => {
  if (isInitial) {
    loading.value = true;
    page.value = 1;
  } else {
    loadingMore.value = true;
  }
  error.value = '';
  try {
    const p = isInitial ? 1 : page.value + 1;
    const res = await axios.get('/api/orders', {
      params: { page: p, page_size: pageSize },
    });
    if (isSuccessResponse(res)) {
      const data = extractData(res);
      totalCount.value = typeof data?.total === 'number' ? data.total : 0;
      const list = data?.orders || [];
      if (isInitial) {
        orders.value = list;
        page.value = 1;
      } else {
        page.value = p;
        orders.value = [...orders.value, ...list];
      }
    } else {
      const errorInfo = handleApiError({ response: res });
      error.value = errorInfo.message || '获取订单列表失败';
    }
  } catch (err) {
    const errorInfo = handleApiError(err);
    error.value = errorInfo.message || '获取订单列表失败';
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

const loadMoreOrders = () => {
  if (!hasMoreOrders.value || loadingMore.value) return;
  fetchOrders(false);
};

const canDownloadResult = (order) => {
  return !!order && order.status === 'paid' && order.task_status === 'completed' && !!(order.file_id || '').trim();
};

const showToast = (msg, isError = true) => {
  toast.value = msg;
  toastError.value = isError;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value = ''; }, 4000);
};

const parseJsonPayload = async (data) => {
  if (!data) return null;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      return JSON.parse(await data.text());
    } catch {
      return null;
    }
  }
  if (typeof data === 'object' && typeof data.message === 'string') {
    return data;
  }
  return null;
};

const isFileMissing = (payload, httpStatus) => {
  return payload?.code === BusinessCode.FILE_NOT_FOUND || httpStatus === 404;
};

const messageFromDownloadFailure = (payload, httpStatus, fallback) => {
  if (isFileMissing(payload, httpStatus)) {
    return FILE_MISSING_HINT;
  }
  const msg = (payload?.message || '').trim();
  return msg || fallback;
};

const downloadResult = async (order) => {
  if (!canDownloadResult(order) || downloading.value) return;
  const fileID = (order.file_id || '').trim();
  downloading.value = order.order_no;
  error.value = '';
  try {
    const res = await axios.get('/api/download', {
      params: { file_id: fileID },
      responseType: 'blob',
    });
    const contentType = res.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const payload = await parseJsonPayload(res.data);
      showToast(messageFromDownloadFailure(payload, res.status, '下载处理结果失败'));
      return;
    }
    let filename = 'processed_phone_data.csv';
    const disposition = res.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
    if (filenameMatch && filenameMatch[1]) {
      filename = decodeURIComponent(filenameMatch[1]);
    }
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    const payload = await parseJsonPayload(err.response?.data);
    const httpStatus = err.response?.status;
    if (payload || httpStatus) {
      showToast(messageFromDownloadFailure(payload, httpStatus, '下载处理结果失败'));
    } else {
      const errorInfo = handleApiError(err);
      showToast(errorInfo.message || '下载处理结果失败');
    }
  } finally {
    downloading.value = '';
  }
};

const handleRefund = (order) => {
  refundTarget.value = order;
  refundReason.value = '';
  showRefundDialog.value = true;
};

const cancelRefund = () => {
  showRefundDialog.value = false;
  refundTarget.value = null;
  refundReason.value = '';
};

const confirmRefund = async () => {
  const order = refundTarget.value;
  if (!order) return;
  const reason = (refundReason.value || '').trim();
  refundSubmitting.value = order.order_no;
  try {
    const res = await axios.post('/api/order/refund', { order_no: order.order_no, reason });
    if (isSuccessResponse(res)) {
      order.status = 'paid';
      order.refund_request_status = 'pending';
      showRefundDialog.value = false;
      refundTarget.value = null;
      refundReason.value = '';
    } else {
      const errorInfo = handleApiError({ response: res });
      error.value = errorInfo.message || '提交失败';
    }
  } catch (err) {
    const errorInfo = handleApiError(err);
    error.value = errorInfo.message || '提交失败';
  } finally {
    refundSubmitting.value = '';
  }
};

onMounted(() => {
  if (!isLoggedIn()) {
    router.replace({ path: '/single', query: { auth: 'login', redirect: '/orders' } });
    return;
  }
  fetchOrders(true);
});

onUnmounted(() => {
  if (toastTimer) clearTimeout(toastTimer);
});
</script>

<style scoped>
.glass-effect {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
